import {
  ArrowRightIcon,
  CheckCircleIcon,
  EyeIcon
} from '@heroicons/react/20/solid';
import {
  ArchiveBoxIcon,
  CubeIcon,
  ShoppingCartIcon,
  TagIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { type Ref, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import useProductUpdateInputModal from 'src/features/product/product-detail/hooks/useProductUpdateInputModal';
import { FF_AUTO_QUOTE_WITH_SAMPLE } from 'src/settings';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { CTA_RFQ_SUPPORT } from 'src/shared/constants/update';
import { useFeatureFlag } from 'src/shared/hooks';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';
import { ProductById } from 'src/shared/types/product';
import { OrderQuote } from 'src/shared/types/quote';
import { Sampling } from 'src/shared/types/sampling';
import { Address } from 'src/shared/types/user';
import { formatMoney } from 'src/shared/utils/formatMoney';

import { Chip, cn, useDisclosure } from 'src/shared/components/ui';
import { ButtonV2 } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

import { purchaseAutoQuote } from '../../api/purchaseAutoQuote';
import extractProductDescription, {
  MIN_DESCRIPTION_LENGTH
} from '../../utils/extractProductDescription';
import AddressSelectionModal from './AddressSelectionModal';
import CavelaManufacturingDrawer from './CavelaManufacturingDrawer';

/** Styling for the Cavela Direct quote card shell (shared with the full-screen quote flow). */
export const CAVELA_DIRECT_QUOTE_CARD_CLASS =
  'w-full rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 transition-all';

const CAVELA_DIRECT_QUOTE_CARD_BOX_SHADOW = [
  '0 0 8px rgba(0, 14, 239, 0.1)',
  '0 0 24px rgba(0, 14, 239, 0.065)',
  '0 0 44px rgba(0, 14, 239, 0.042)',
  '0 0 68px rgba(0, 14, 239, 0.028)',
  '0 0 96px rgba(0, 14, 239, 0.018)'
].join(', ');

/** Normalized quote data used for display. */
interface QuoteDisplayData {
  quantity: number;
  ddpUnitCost: number;
  productionDays: number;
  totalLeadTimeDays: number;
  totalPrice: number;
  /** Flat sample fee for this quote. 0 when the quote is not a Cavela Direct auto-quote. */
  sampleUnitPriceUsd: number;
  /**
   * True when the sample timeline records payment, or when the product quote's
   * payment intent status is `completed` (fallback for backend sync gaps).
   */
  samplePurchased: boolean;
  /**
   * Id of the Sampling record created for this quote, if any. Used to deep-link
   * into the Samples tab and auto-open the purchased sample's modal once the
   * customer has paid for the pre-production sample.
   */
  sampleId: string | null;
  /**
   * Id of the BulkOrdering record matching this quote, if any. Used to
   * deep-link into the Orders tab and auto-open the purchased order's modal
   * once the customer has paid for the bulk order.
   */
  bulkOrderingId: string | null;
  /**
   * True when the matched BulkOrdering has at least one Stripe payment intent
   * with status 'completed' — i.e. the bulk order has been paid for.
   * Computed off the BulkOrdering rather than `samplePurchased` because
   * `ProductQuote.sample_payment_intents` is shared between sample and bulk
   * auto-quote payments and can't distinguish them.
   */
  bulkPaymentCompleted: boolean;
}

type ProductQuoteSample =
  OrderQuote['product_quotes'][number]['samples'][number];

/** Most recently requested non-archived sample on this product quote. */
function pickMostRecentSample(
  pq: OrderQuote['product_quotes'][number]
): ProductQuoteSample | null {
  const candidates = (pq.samples ?? []).filter((s) => !s.archived);

  if (candidates.length === 0) return null;
  const [mostRecent] = [...candidates].sort((a, b) => {
    const aTs = Date.parse(a.requested_at);
    const bTs = Date.parse(b.requested_at);

    return (isNaN(bTs) ? 0 : bTs) - (isNaN(aTs) ? 0 : aTs);
  });

  return mostRecent ?? null;
}

/** Paid when the sampling timeline records the `paid` stage as started. */
function isSamplePaidFromTimeline(sample: Sampling): boolean {
  const paidStage = sample.timeline?.find((entry) => entry.stage === 'paid');

  return paidStage?.started_at != null;
}

/**
 * Find the BulkOrdering associated with a given ProductQuote, if any.
 * Picks the most recently created match so any back-fills line up with the
 * latest order.
 */
function pickBulkOrderingForQuote(
  pq: OrderQuote['product_quotes'][number],
  bulkOrderings: BulkOrdering[]
): BulkOrdering | null {
  const matches = bulkOrderings.filter((bo) => bo.product_quote?.id === pq.id);

  if (matches.length === 0) return null;
  const [mostRecent] = [...matches].sort((a, b) => {
    const aTs = Date.parse(a.created_at);
    const bTs = Date.parse(b.created_at);

    return (isNaN(bTs) ? 0 : bTs) - (isNaN(aTs) ? 0 : aTs);
  });

  return mostRecent ?? null;
}

const STAFF_ONLY_ORDER_QUOTE_STATUSES = new Set<OrderQuote['status']>([
  'draft',
  'ready'
]);

/** Order quotes the customer has committed to (e.g. after sample payment). */
const CHOSEN_ORDER_QUOTE_STATUSES = new Set<OrderQuote['status']>([
  'chosen',
  'sampling'
]);

type AutoQuoteCandidate = {
  orderQuote: OrderQuote;
  pq: OrderQuote['product_quotes'][number];
};

function isDisplayEligibleOrderQuote(oq: OrderQuote): boolean {
  if (STAFF_ONLY_ORDER_QUOTE_STATUSES.has(oq.status)) return false;

  // Backend may return rejected even though it is not in the TS union yet.
  return (oq.status as string) !== 'rejected';
}

function compareOrderQuotesByUpdatedAtDesc(
  a: OrderQuote,
  b: OrderQuote
): number {
  const aTs = Date.parse(a.updated_at);
  const bTs = Date.parse(b.updated_at);

  return (isNaN(bTs) ? 0 : bTs) - (isNaN(aTs) ? 0 : aTs);
}

function pickBestAutoQuoteCandidate(
  candidates: AutoQuoteCandidate[]
): AutoQuoteCandidate | null {
  const eligible = candidates.filter((c) =>
    isDisplayEligibleOrderQuote(c.orderQuote)
  );

  if (eligible.length === 0) return null;

  const chosenTier = eligible.filter((c) =>
    CHOSEN_ORDER_QUOTE_STATUSES.has(c.orderQuote.status)
  );
  const surfacedTier = eligible.filter(
    (c) => c.orderQuote.status === 'surfaced'
  );
  const pool =
    chosenTier.length > 0
      ? chosenTier
      : surfacedTier.length > 0
        ? surfacedTier
        : eligible;

  const [best] = [...pool].sort((a, b) =>
    compareOrderQuotesByUpdatedAtDesc(a.orderQuote, b.orderQuote)
  );

  return best ?? null;
}

/**
 * Pick the ProductQuote that should drive the Cavela Direct summary card.
 * Prefers auto-quote product quotes (`cavela_direct_sample_unit_price_usd`), then
 * order quotes in status priority: chosen/sampling → surfaced → other eligible.
 * Within each tier, the most recently updated parent order quote wins.
 */
function pickProductQuoteForDisplay(
  orderQuotes: OrderQuote[]
): OrderQuote['product_quotes'][number] | null {
  const autoQuoteCandidates: AutoQuoteCandidate[] = orderQuotes.flatMap(
    (orderQuote) =>
      orderQuote.product_quotes
        .filter((pq) => pq.cavela_direct_sample_unit_price_usd != null)
        .map((pq) => ({ orderQuote, pq }))
  );

  const bestAutoQuote = pickBestAutoQuoteCandidate(autoQuoteCandidates);

  if (bestAutoQuote) return bestAutoQuote.pq;

  const [mostRecentCustomerOrderQuote] = [...orderQuotes]
    .filter(isDisplayEligibleOrderQuote)
    .sort(compareOrderQuotesByUpdatedAtDesc);

  return mostRecentCustomerOrderQuote?.product_quotes[0] ?? null;
}

/**
 * Extract display data from a real ProductQuote (from the quotes API).
 */
function extractFromOrderQuotes(
  orderQuotes: OrderQuote[],
  bulkOrderings: BulkOrdering[]
): QuoteDisplayData | null {
  const pq = pickProductQuoteForDisplay(orderQuotes);

  if (!pq) return null;

  const ddp = parseFloat(pq.ddp_unit_price_marked_up ?? '0');
  const qty = pq.quantity ?? 0;
  const matchedBulkOrdering = pickBulkOrderingForQuote(pq, bulkOrderings);
  const bulkPaymentCompleted =
    matchedBulkOrdering?.sample_payment_intents?.some(
      (pi) => pi.status === 'completed'
    ) ?? false;
  const sample = pickMostRecentSample(pq);
  const samplePurchased =
    (sample != null && isSamplePaidFromTimeline(sample)) ||
    pq.sample_payment_status === 'completed';
  const sampleId = samplePurchased && sample != null ? sample.id : null;

  return {
    quantity: qty,
    ddpUnitCost: ddp,
    productionDays: pq.production_time_days ?? 0,
    totalLeadTimeDays: pq.lead_time_days_with_shipping ?? 0,
    totalPrice: ddp * qty,
    sampleUnitPriceUsd: parseFloat(
      pq.cavela_direct_sample_unit_price_usd ?? '0'
    ),
    samplePurchased,
    sampleId,
    bulkOrderingId: matchedBulkOrdering?.id ?? null,
    bulkPaymentCompleted
  };
}

interface CavelaDirectQuoteSummaryProps {
  cardRef?: Ref<HTMLDivElement> | null;
  product?: ProductById;
  /** Raw auto-quote result — used on the animation page and as fallback. */
  orderQuotes?: OrderQuote[];
  /**
   * Bulk orderings for the current product. Used to detect when the bulk
   * order has been paid for so we can swap the Purchase button for a
   * "View order" deep link.
   */
  bulkOrderings?: BulkOrdering[];
  onDismissAnimate?: () => void;
  /** Called after the Purchase button succeeds and the Stripe tab opens. */
  onPurchaseComplete?: () => void;
  /** Opens the Cavela Direct RFQ slide-over on the given tab. */
  onOpenRfqDrawer?: (tab: 'rfq' | 'timeline') => void;
  isProductPage?: boolean;
}

const CavelaDirectQuoteSummary: React.FC<CavelaDirectQuoteSummaryProps> = ({
  cardRef,
  product,
  orderQuotes,
  bulkOrderings,
  onDismissAnimate,
  onPurchaseComplete,
  onOpenRfqDrawer,
  isProductPage = false
}) => {
  const navigate = useNavigate();
  const { setData: setUpdateInputData } = useProductUpdateInputModal();
  const { isFeatureEnabled: isAutoQuoteWithSampleEnabled } = useFeatureFlag(
    FF_AUTO_QUOTE_WITH_SAMPLE
  );
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  // Which purchase the user just initiated. Read by the address-modal confirm
  // handler to decide whether to include the pre-production sample.
  const [pendingPurchaseKind, setPendingPurchaseKind] = useState<
    'bulk' | 'sample' | null
  >(null);
  const {
    isOpen: isAddressModalOpen,
    onOpen: openAddressModal,
    onOpenChange: onAddressModalOpenChange,
    onClose: closeAddressModal
  } = useDisclosure();
  const {
    isOpen: isManufacturingDrawerOpen,
    onOpenChange: onManufacturingDrawerOpenChange
  } = useDisclosure();

  const displayData = useMemo<QuoteDisplayData | null>(() => {
    if (orderQuotes && orderQuotes.length > 0) {
      const fromQuotes = extractFromOrderQuotes(
        orderQuotes,
        bulkOrderings ?? []
      );

      if (fromQuotes) return fromQuotes;
    }

    return null;
  }, [orderQuotes, bulkOrderings]);

  const productDescription = useMemo(() => {
    const routerQuoteSummary = product?.router_quote_summary;

    if (routerQuoteSummary) {
      return routerQuoteSummary;
    }
    const documentDescription =
      product?.rfq?.document?.description?.trim() || null;

    if (
      documentDescription &&
      documentDescription.length >= MIN_DESCRIPTION_LENGTH
    ) {
      return documentDescription;
    }
    const { description: fromMarkdown } = extractProductDescription(
      product?.rfq?.rfq_markdown
    );

    return fromMarkdown || documentDescription;
  }, [product?.rfq?.rfq_markdown, product?.rfq?.document?.description]);

  const hasSampleOrder = useMemo(
    () =>
      orderQuotes?.some((oq) =>
        oq.product_quotes?.some((pq) => pq.samples?.length > 0)
      ) ?? false,
    [orderQuotes]
  );

  const displayedTotalUsd = useMemo(() => {
    if (!displayData) return 0;

    // When a sample order already exists, the headline total reflects the
    // sample. Otherwise it always shows the bulk total — the sample now has
    // its own price+CTA inside the dedicated sample card.
    // if (hasSampleOrder) {
    //   return displayData.sampleUnitPriceUsd;
    // }
    return displayData.totalPrice;
  }, [displayData, hasSampleOrder]);

  const purchaseMutation = useMutation({
    mutationFn: purchaseAutoQuote,
    onSuccess: (data) => {
      setPendingPurchaseKind(null);
      window.open(data.stripe_payment_link, '_blank');
      onPurchaseComplete?.();
    },
    onError: (error: Error) => {
      setPurchaseError(error.message || 'Failed to create payment link.');
    }
  });

  const handlePurchase = (kind: 'bulk' | 'sample') => {
    if (!product?.id || !displayData) return;

    // Both bulk and sample go through the address modal so the backend can
    // mint a fresh Stripe link for the correct amount (sample fee vs. full
    // bulk total). Reusing the cached sample_payment_link for bulk would
    // charge only the flat sample fee.
    setPendingPurchaseKind(kind);
    openAddressModal();
  };

  const handleAddressConfirm = (_address: Address) => {
    if (!product?.id || !displayData || !pendingPurchaseKind) return;

    closeAddressModal();

    setPurchaseError(null);
    purchaseMutation.mutate({
      product_id: product.id,
      quantity: displayData.quantity,
      include_sample:
        pendingPurchaseKind === 'sample' && isAutoQuoteWithSampleEnabled
    });
  };

  if (!displayData) return null;

  return (
    <div
      ref={cardRef}
      className={cn(
        CAVELA_DIRECT_QUOTE_CARD_CLASS,
        'w-full min-w-0 !transition-none'
      )}
      style={{
        boxShadow: isProductPage ? 'none' : CAVELA_DIRECT_QUOTE_CARD_BOX_SHADOW
      }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
        <div className="mx-auto flex shrink-0 flex-col gap-3 sm:mx-0">
          {product?.image ? (
            <img
              src={product.image}
              alt={product.detail.name}
              className="h-[400px] w-[400px] shrink-0 rounded-lg border border-gray-200 object-cover"
            />
          ) : (
            <div
              className="h-[284px] w-[284px] shrink-0 rounded-lg border border-gray-200 bg-gray-200"
              aria-hidden
            />
          )}
          {product?.image ? (
            <div className="flex items-center gap-2 mt-2 justify-center">
              {[0].map((i) => (
                <img
                  key={i}
                  src={product.image ?? undefined}
                  alt=""
                  aria-hidden
                  className={cn(
                    'h-[60px] w-[88px] shrink-0 rounded-md border object-cover',
                    i === 0 ? 'border-primary border-2' : 'border-gray-200'
                  )}
                />
              ))}
            </div>
          ) : null}
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Typography
              variant="h5"
              weight="bold"
              as="p"
              className="leading-snug sm:!text-[22px] sm:!tracking-[-0.22px]"
            >
              {product?.detail.name}
            </Typography>
            <Typography
              variant="h3"
              weight="medium"
              as="p"
              className="text-[14px] flex items-center gap-1"
            >
              Made by:{' '}
              <button
                type="button"
                // hide for now
                // onClick={openManufacturingDrawer}
                // className="text-primary cursor-pointer opacity-100 hover:opacity-70 transition-opacity underline bg-transparent border-0 p-0 font-inherit"
                className="cursor-auto"
              >
                Cavela Manufacturing
              </button>{' '}
              {/* <CountryFlag countryCode="CN" /> */}
            </Typography>
          </div>
          <div>
            {productDescription && (
              <Typography
                variant="body-2"
                as="p"
                className="text-secondary-text leading-relaxed whitespace-pre-line"
              >
                {productDescription}
              </Typography>
            )}
            {onOpenRfqDrawer && (
              <div className="mt-2 flex items-center gap-2">
                <ButtonV2
                  size="sm"
                  color="fourth"
                  className="h-6 text-primary"
                  onPress={() => onOpenRfqDrawer('rfq')}
                  startContent={
                    <EyeIcon className="size-4 pointer-events-none" />
                  }
                >
                  View more details
                </ButtonV2>
                {/* // bring back in 0.3 cavela direct release */}
                {/* <ButtonV2
                  size="sm"
                  color="tertiary"
                  className="h-8"
                  onPress={() => onOpenRfqDrawer('timeline')}
                >
                  View Timeline
                </ButtonV2> */}
              </div>
            )}
          </div>
          {isAutoQuoteWithSampleEnabled && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {displayData.samplePurchased ? (
                    <CheckCircleIcon className="size-5 shrink-0 text-success-500" />
                  ) : (
                    <ArchiveBoxIcon className="size-5 shrink-0 text-primary-text" />
                  )}
                  <Typography
                    variant="body-1"
                    weight="bold"
                    as="p"
                    className="text-primary-text"
                  >
                    Pre-production sample
                  </Typography>
                </div>
                {displayData.samplePurchased ? (
                  <Chip
                    size="sm"
                    variant="flat"
                    color="success"
                    className="relative left-1"
                  >
                    Payment complete
                  </Chip>
                ) : null}
              </div>
              <Typography
                variant="body-2"
                as="p"
                className="text-secondary-text mt-1"
              >
                Receive a single sample before full production to verify
                quality, fit, and materials. You will receive the sample within
                20 to 35 days.
              </Typography>
              <div className="mt-3 flex items-center justify-between gap-4">
                <Typography
                  variant="h4"
                  weight="bold"
                  as="p"
                  className="text-primary-text"
                >
                  {formatMoney(displayData.sampleUnitPriceUsd)}
                </Typography>
                {displayData.samplePurchased ? (
                  displayData.sampleId && product?.id ? (
                    <Link
                      to={`${ROUTE_PATHS.PRODUCTS}/${product.id}?tab=samples&sample=${displayData.sampleId}`}
                      className="text-primary underline hover:opacity-70 transition-opacity text-sm font-medium"
                    >
                      View sample
                    </Link>
                  ) : null
                ) : (
                  <ButtonV2
                    size="md"
                    color="tertiary"
                    onPress={() => handlePurchase('sample')}
                    isDisabled={purchaseMutation.isPending}
                    isLoading={
                      purchaseMutation.isPending &&
                      pendingPurchaseKind === 'sample'
                    }
                  >
                    Purchase sample
                  </ButtonV2>
                )}
              </div>
            </div>
          )}
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                {displayData.bulkPaymentCompleted ? (
                  <CheckCircleIcon className="size-5 shrink-0 text-success-500" />
                ) : (
                  <ShoppingCartIcon className="size-5 shrink-0 text-primary-text" />
                )}
                <Typography
                  variant="body-1"
                  weight="bold"
                  as="p"
                  className="text-primary-text"
                >
                  Order summary
                </Typography>
              </div>
              {displayData.bulkPaymentCompleted && (
                <Chip
                  size="sm"
                  variant="flat"
                  color="success"
                  className="relative left-1"
                >
                  Payment complete
                </Chip>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1">
                  <CubeIcon className="size-4 shrink-0 text-secondary-text" />
                  <Typography
                    variant="body-2"
                    as="p"
                    className="text-secondary-text"
                  >
                    Quantity
                  </Typography>
                </div>
                <Typography
                  variant="body-1"
                  weight="medium"
                  as="p"
                  className="text-primary-text"
                >
                  {displayData.quantity.toLocaleString()} units
                </Typography>
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1">
                  <TagIcon className="size-4 shrink-0 text-secondary-text" />
                  <Typography
                    variant="body-2"
                    as="p"
                    className="text-secondary-text"
                  >
                    Unit price
                  </Typography>
                </div>
                <Typography
                  variant="body-1"
                  weight="medium"
                  as="p"
                  className="text-primary-text"
                >
                  {formatMoney(displayData.ddpUnitCost)} each
                </Typography>
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1">
                  <TruckIcon className="size-4 shrink-0 text-secondary-text" />
                  <Typography
                    variant="body-2"
                    as="p"
                    className="text-secondary-text"
                  >
                    Delivery
                  </Typography>
                </div>
                <Typography
                  variant="body-1"
                  weight="medium"
                  as="p"
                  className="text-primary-text"
                >
                  {displayData.productionDays}–{displayData.totalLeadTimeDays}{' '}
                  days
                </Typography>
              </div>
            </div>
            <hr className="my-3 border-gray-200" />
            <div className="flex items-center justify-between gap-4">
              <Typography
                variant="body-1"
                as="p"
                weight="bold"
                className="text-primary-text"
              >
                TOTAL
              </Typography>
              <Typography
                variant="h1"
                weight="bold"
                as="p"
                className="shrink-0 text-right !text-[30px] !font-bold tracking-tight sm:!text-[36px]"
              >
                {formatMoney(displayedTotalUsd)}
              </Typography>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              {displayData.bulkPaymentCompleted &&
              displayData.bulkOrderingId ? (
                <ButtonV2
                  fullWidth
                  color="primary"
                  size="xl"
                  className="font-bold text-lg flex-1"
                  onPress={() =>
                    navigate(
                      `${ROUTE_PATHS.PRODUCTS}/${product?.id}?tab=orders&order=${displayData.bulkOrderingId}`
                    )
                  }
                  endContent={
                    <ArrowRightIcon className="size-5 pointer-events-none" />
                  }
                >
                  View order
                </ButtonV2>
              ) : (
                <ButtonV2
                  fullWidth
                  color="primary"
                  size="xl"
                  className="font-bold text-lg flex-1"
                  onPress={() => handlePurchase('bulk')}
                  isDisabled={purchaseMutation.isPending}
                  isLoading={
                    purchaseMutation.isPending && pendingPurchaseKind === 'bulk'
                  }
                >
                  Purchase
                </ButtonV2>
              )}
              {!onDismissAnimate && (
                <ButtonV2
                  fullWidth
                  color="tertiary"
                  size="xl"
                  className="flex-1"
                  onPress={() => {
                    setUpdateInputData(
                      { name: product?.rfq?.document?.name ?? '' },
                      CTA_RFQ_SUPPORT
                    );
                  }}
                >
                  Message us
                </ButtonV2>
              )}
              {onDismissAnimate && (
                <ButtonV2
                  fullWidth
                  variant="light"
                  color="default"
                  size="xl"
                  className="text-secondary-text flex-1"
                  onPress={onDismissAnimate}
                >
                  Not for now
                </ButtonV2>
              )}
            </div>
            {purchaseError && (
              <Typography
                variant="body-2"
                as="p"
                className="text-red-500 text-center"
              >
                {purchaseError}
              </Typography>
            )}
          </div>
        </div>
      </div>

      <AddressSelectionModal
        isOpen={isAddressModalOpen}
        onOpenChange={onAddressModalOpenChange}
        onConfirm={handleAddressConfirm}
        title="Select shipping address"
        confirmLabel="Continue to payment"
        isLoading={purchaseMutation.isPending}
      />

      <CavelaManufacturingDrawer
        isOpen={isManufacturingDrawerOpen}
        onOpenChange={onManufacturingDrawerOpenChange}
      />
    </div>
  );
};

export default CavelaDirectQuoteSummary;
